<template>
	<ActionMenu
		@dropdownIsOpen="companyDropdown"
		align="bottom-end"
		menu-class="max-h-[60vh]"
	>
		<template #toggle>
			<button
				type="button"
				class="flex cursor-pointer items-center gap-2 py-1.5 text-white focus-visible:ring-2 focus-visible:ring-white"
				:aria-label="$org.name"
				:aria-expanded="companyDropdownOpen.toString()"
			>
				<IconBuilding />
				<IconChevronDown
					class="transition-transform"
					:class="companyDropdownOpen ? 'rotate-180' : ''"
				/>
			</button>
		</template>

		<div
			class="flex max-h-[calc(60vh-1rem)] w-64 max-w-[calc(100vw-1.5rem)] flex-col"
		>
			<div class="shrink-0">
				<!-- Dropdown heading -->
				<div class="px-4 py-2 font-bold text-grey-1000">
					{{ $org.name }}
				</div>

				<!-- Organization profile -->
				<ActionMenuItem
					:href="organizationSlug"
					target="_blank"
					rel="noopener noreferrer"
				>
					<template #icon><IconExternalLink size="16"/></template>
					{{ $t('organization-profile.career-page') }}
				</ActionMenuItem>

				<!-- Organization edit -->
				<ActionMenuItem
					v-if="$can('edit organization')"
					to="/organization/profile"
					:aria-current="isSettingsActive ? 'page' : null"
					:active="isSettingsActive"
				>
					<template #icon
						><img
							src="/icons/organization/settings.svg"
							alt=""
							class="h-4 w-4 shrink-0"
					/></template>
					{{ $t('organization-profile.settings') }}
				</ActionMenuItem>

				<!-- Members -->
				<ActionMenuItem v-if="$can('list member')" to="/organization/user">
					<template #icon><IconUsers size="16"/></template>
					{{ $t('organization.members') }}
				</ActionMenuItem>

				<!-- Pricing -->
				<ActionMenuItem
					v-if="$can('manage organization billing')"
					to="/organization/pricing"
				>
					<template #icon
						><img
							src="/icons/organization/subscription.svg"
							alt=""
							class="h-4 w-4 shrink-0"
					/></template>
					{{ $t('organization.subscription') }}
				</ActionMenuItem>
			</div>

			<div
				v-if="hasSwitchableOrganizations"
				class="mt-1.5 flex min-h-0 flex-1 flex-col border-t border-grey-200 pt-1.5"
			>
				<div
					class="flex shrink-0 items-center justify-between gap-3 px-4 py-2"
				>
					<div class="text-xs font-semibold uppercase text-grey-700">
						{{ $t('organization-profile.change-company') }}
					</div>

					<button
						type="button"
						class="mb-1 inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-normal text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-grey-1000"
						data-test="organization-search-toggle"
						:aria-expanded="isCompanySearchOpen.toString()"
						aria-controls="organization-search-panel"
						@click="toggleCompanySearch"
					>
						<span>{{ $t('common.search') }}</span>
						<IconChevronDown
							size="16"
							class="shrink-0 transition-transform"
							:class="isCompanySearchOpen ? 'rotate-180' : ''"
						/>
					</button>
				</div>

				<div
					v-show="isCompanySearchOpen"
					id="organization-search-panel"
					data-test="organization-search-panel"
					class="shrink-0 px-4 pb-2 text-grey-1000 font-normal"
				>
					<label class="sr-only" :for="companySearchInputId">
						{{ $t('common.search') }}
					</label>
					<BaseInput
						name="organization-search"
						:input-id="companySearchInputId"
						v-model="companySearchQuery"
						:placeholder="$t('common.search')"
						:leading-icon="searchLeadingIcon"
						class="font-normal"
						clearable
					/>
				</div>

				<div class="min-h-0 overflow-y-auto pb-1">
					<ActionMenuItem
						v-for="organization in filteredOrganizations"
						:key="organization.id"
						data-test="organization-option"
						truncate
						@click="changeOrganization(organization)"
					>
						<template #icon><IconBuilding size="16"/></template>
						{{ organization.name }}
					</ActionMenuItem>

					<div
						v-if="filteredOrganizations.length === 0"
						class="px-4 py-3 text-center text-sm text-grey-700"
					>
						{{ $t('general.no-search-results') }}
					</div>
				</div>
			</div>
		</div>
	</ActionMenu>
</template>

<script>
import ActionMenu from './ActionMenu.vue'
import ActionMenuItem from './ActionMenuItem.vue'
import { BaseInput } from '~/components/Form'
import {
	IconBuilding,
	IconExternalLink,
	IconUsers,
	IconChevronDown,
	IconMagnifyingGlass
} from '~/components/Icons'

export default {
	components: {
		ActionMenu,
		ActionMenuItem,
		BaseInput,
		IconBuilding,
		IconExternalLink,
		IconUsers,
		IconChevronDown
	},
	data() {
		return {
			companyDropdownOpen: false,
			isCompanySearchOpen: false,
			companySearchQuery: ''
		}
	},
	computed: {
		isSettingsActive() {
			return (
				this.$route.path.replace(/\/$/, '') === '/organization/profile'
			)
		},
		organizations() {
			return this.$user?.organizations || []
		},
		currentOrganizationId() {
			return this.$user?.current_organization?.id ?? null
		},
		switchableOrganizations() {
			const currentOrganizationId = this.currentOrganizationId

			return this.organizations
				.filter(organization => {
					return (
						organization?.name &&
						String(organization.id) !==
							String(currentOrganizationId)
					)
				})
				.sort((first, second) =>
					first.name.localeCompare(second.name, undefined, {
						numeric: true,
						sensitivity: 'base'
					})
				)
		},
		filteredOrganizations() {
			const query = this.companySearchQuery.trim().toLowerCase()

			if (!query) {
				return this.switchableOrganizations
			}

			return this.switchableOrganizations.filter(organization =>
				organization.name.toLowerCase().includes(query)
			)
		},
		hasSwitchableOrganizations() {
			return this.switchableOrganizations.length > 0
		},
		searchLeadingIcon() {
			return IconMagnifyingGlass
		},
		companySearchInputId() {
			return 'organization-search-input'
		},
		canCreateNewOrganization() {
			// During beta phase, users cannot create multiple organizations
			// The backend prevents users from creating new organizations if they are already members of any organization
			return false
		},
		organizationSlug() {
			return `/${this.$org.slug}`
		}
	},
	methods: {
		companyDropdown(value) {
			this.companyDropdownOpen = value
			if (!value) {
				this.isCompanySearchOpen = false
				this.companySearchQuery = ''
			}
		},
		toggleCompanySearch() {
			this.isCompanySearchOpen = !this.isCompanySearchOpen

			if (!this.isCompanySearchOpen) {
				this.companySearchQuery = ''
			}
		},
		changeOrganization(organization) {
			this.$store.dispatch('user/changeOrganization', organization)
		}
	}
}
</script>
